import React, { useId } from 'react';
import { cn } from '@/lib/utils';

interface SettingsGroupProps {
  title?: string;
  description?: string;
  /** Sits under the box: persistence notes and caveats. */
  footnote?: React.ReactNode;
  /** `false` when the child already draws its own border, as DataTable does. */
  boxAs?: 'div' | 'dl' | 'ul' | false;
  className?: string;
  children: React.ReactNode;
}

const BOX = 'divide-y divide-border overflow-hidden rounded-xl border bg-card shadow-sm';

/**
 * One bordered container per section, rows divided by a hairline inside it —
 * the shape GitHub, Linear and Raycast all converge on. The heading sits above
 * the box rather than in a padded header, which is what a card wrapped around a
 * single control was mostly spending its height on.
 */
const SettingsGroup: React.FC<SettingsGroupProps> = ({
  title,
  description,
  footnote,
  boxAs = 'div',
  className,
  children,
}) => {
  const headingId = useId();

  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cn('@container/section', className)}
    >
      {title && (
        <div className="mb-3">
          <h2 id={headingId} className="revy-cjk text-sm font-semibold leading-[1.5] tracking-tight">
            {title}
          </h2>
          {description && (
            <p className="revy-cjk mt-1 max-w-[40em] text-xs leading-[1.7] tracking-[0.02em] text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      )}

      {boxAs === false ? children : React.createElement(boxAs, { className: BOX }, children)}

      {footnote && (
        <p className="revy-cjk mt-2 max-w-[40em] text-xs leading-[1.7] tracking-[0.02em] text-muted-foreground">
          {footnote}
        </p>
      )}
    </section>
  );
};

export default SettingsGroup;
